import React, { useState } from 'react';
import { ConicGlowCard } from '../common/ConicGlowCard';
import {
  CheckIcon,
  ShieldCheckIcon,
  CalendarIcon,
  UsersIcon,
  SparklesIcon,
  ClockIcon,
  ArrowLeftIcon
} from '../common/Icons';

interface ConfirmationGateDeckProps {
  draftData?: any;
  onConfirm: (payload: {
    customerName: string;
    customerPhone: string;
    customerEmail: string;
    date: string;
    guestCount: number;
    slot: 'lunch' | 'dinner';
  }) => Promise<void>;
  onCancel?: () => void;
  onTouchAction?: (action: string, data?: any) => void;
}

export const ConfirmationGateDeck: React.FC<ConfirmationGateDeckProps> = ({
  draftData,
  onConfirm,
  onCancel,
  onTouchAction
}) => {
  const initialData = draftData?.draft || draftData || {};
  const fallbackVenue = draftData?.venue || initialData.venue || null;

  const [customerName, setCustomerName] = useState(initialData.customerName || '');
  const [customerPhone, setCustomerPhone] = useState(initialData.customerPhone || '');
  const [customerEmail, setCustomerEmail] = useState(initialData.customerEmail || '');
  const [eventDate, setEventDate] = useState(initialData.date || initialData.eventDate || '2026-11-25');
  const [guestCount, setGuestCount] = useState<number>(initialData.guestCount || 400);
  const [slot, setSlot] = useState<'lunch' | 'dinner'>(initialData.slot || 'dinner');
  const [eventType, setEventType] = useState<string>(initialData.eventType || 'barat');
  const [specialNotes, setSpecialNotes] = useState<string>('');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccessPulse, setIsSuccessPulse] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const venueName = initialData.venueName || fallbackVenue?.name || 'Majestic Grand Banquet';
  const rawPkgName =
    initialData.packageName ||
    draftData?.selectedPackage?.name ||
    fallbackVenue?.packages?.[0]?.name;
  const packageName =
    rawPkgName && !/chicken|mutton|beef|dish/i.test(String(rawPkgName))
      ? rawPkgName
      : 'Peak Season Hall Rental';
  const fallbackPkgPrice =
    draftData?.selectedPackage?.pricePkr ||
    fallbackVenue?.packages?.find((p: any) => p.packageType?.includes('flat_rental'))?.pricePkr ||
    285000;
  const totalEstimatedPkr =
    initialData.totalEstimatedPkr ||
    (fallbackPkgPrice >= 50000 ? fallbackPkgPrice + 45000 : 330000);

  const eventTypeOptions = [
    { key: 'barat', label: 'Barat' },
    { key: 'valima', label: 'Valima' },
    { key: 'mehndi', label: 'Mehndi' },
    { key: 'nikkah', label: 'Nikkah' },
    { key: 'corporate', label: 'Corporate' }
  ];

  const handleQuickFillDemo = () => {
    setCustomerName('Farhan Siddiqui');
    setCustomerPhone('+92 300 8219940');
    setCustomerEmail('farhan.siddiqui@intrepidtech.net');
    setErrorMessage(null);
  };

  const handleCommit = async () => {
    if (!customerName.trim() || !customerPhone.trim() || !customerEmail.trim()) {
      setErrorMessage('Please provide your full name, Pakistani mobile number, and email address.');
      return;
    }

    try {
      setIsSubmitting(true);
      setErrorMessage(null);

      if (onTouchAction) {
        onTouchAction('tap_confirm_hold', {
          venueName,
          guestCount,
          eventDate,
          slot,
          eventType,
          totalEstimatedPkr
        });
      }

      setIsSuccessPulse(true);
      await onConfirm({
        customerName,
        customerPhone,
        customerEmail,
        date: eventDate,
        guestCount,
        slot
      });
    } catch (err: any) {
      setIsSuccessPulse(false);
      setErrorMessage(err?.message || 'Failed to confirm provisional hold. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="stage-deck-content">
      <div className="deck-header" style={{ marginBottom: '0.42rem' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem', minWidth: 0, width: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.45rem', flexWrap: 'nowrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', minWidth: 0 }}>
              {onCancel && (
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={onCancel}
                  style={{ padding: '0.28rem 0.58rem', fontSize: '0.71rem', gap: '0.28rem', flexShrink: 0 }}
                >
                  <ArrowLeftIcon size={12} />
                  <span>Back</span>
                </button>
              )}
              <h2 className="deck-title" style={{ margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                Booking Review
              </h2>
            </div>
            <span className="pill-badge verified" style={{ fontSize: '0.62rem', padding: '0.12rem 0.45rem', flexShrink: 0 }}>
              <ShieldCheckIcon size={10} />
              <span>72h Free Hold</span>
            </span>
          </div>
          <p className="deck-subtitle header-mobile-hide" style={{ margin: 0 }}>
            Verify function shift, guest count, and contact details before issuing your cryptographic <code style={{ color: 'hsl(var(--accent-gold))' }}>MHF-XXXXXX</code> hold voucher
          </p>
        </div>
      </div>

      <div style={{ maxWidth: '52rem', margin: '0 auto' }}>
        <ConicGlowCard
          glowIntensity={isSuccessPulse ? 1.0 : 0.85}
          active={isSuccessPulse}
          style={{
            transition: 'box-shadow 0.4s ease, border-color 0.4s ease',
            boxShadow: isSuccessPulse ? '0 0 50px hsl(var(--success) / 0.6)' : undefined
          }}
        >
          <div style={{ padding: '0.72rem 0.88rem' }}>
            {/* Top Certificate Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'nowrap', gap: '0.5rem', marginBottom: '0.52rem', paddingBottom: '0.48rem', borderBottom: '1px solid hsl(var(--surface-border) / 0.6)' }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <h3 style={{ fontSize: '1rem', fontWeight: 800, fontFamily: 'var(--font-display)', lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {venueName}
                </h3>
                <p style={{ fontSize: '0.67rem', color: 'hsl(var(--text-secondary))', marginTop: '0.06rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  Tier: <strong>{packageName}</strong>
                </p>
              </div>
              <div style={{ textAlign: 'right', padding: '0.25rem 0.55rem', borderRadius: '0.58rem', backgroundColor: 'hsl(var(--surface-elevated) / 0.75)', border: '1px solid hsl(var(--accent-gold) / 0.35)', flexShrink: 0 }}>
                <span style={{ fontSize: '0.55rem', color: 'hsl(var(--text-muted))', display: 'block', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 700 }}>
                  Provisional Total
                </span>
                <span className="num-tabular" style={{ fontSize: '0.92rem', fontWeight: 800, color: 'hsl(var(--accent-gold))' }}>
                  Rs. {totalEstimatedPkr.toLocaleString()}
                </span>
              </div>
            </div>

            {/* Function Type Pill Selector — 5-Column Full-Width Grid (Zero Clipping) */}
            <div style={{ marginBottom: '0.5rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.24rem', fontSize: '0.62rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'hsl(var(--text-muted))', marginBottom: '0.22rem' }}>
                <SparklesIcon size={10} />
                <span>Function / Ceremony Type</span>
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: '0.25rem' }}>
                {eventTypeOptions.map((ev) => (
                  <button
                    key={ev.key}
                    type="button"
                    className={`corridor-chip ${eventType === ev.key ? 'active' : ''}`}
                    style={{ fontSize: '0.64rem', padding: '0.22rem 0.12rem', justifyContent: 'center', width: '100%' }}
                    onClick={() => setEventType(ev.key)}
                  >
                    <span>{ev.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Inline Tap-to-Edit Fields */}
            <div style={{ display: 'grid', gridTemplateColumns: '1.12fr 1.18fr 0.7fr', gap: '0.36rem', marginBottom: '0.52rem' }}>
              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.2rem', fontSize: '0.63rem', fontWeight: 600, color: 'hsl(var(--text-muted))', marginBottom: '0.14rem', whiteSpace: 'nowrap' }}>
                  <CalendarIcon size={11} /> Date
                </label>
                <input
                  type="date"
                  value={eventDate}
                  onChange={(e) => setEventDate(e.target.value)}
                  className="form-input"
                  style={{ padding: '0.32rem 0.36rem', fontSize: '0.7rem' }}
                />
              </div>

              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.2rem', fontSize: '0.63rem', fontWeight: 600, color: 'hsl(var(--text-muted))', marginBottom: '0.14rem', whiteSpace: 'nowrap' }}>
                  <ClockIcon size={11} /> Shift
                </label>
                <select
                  value={slot}
                  onChange={(e) => setSlot(e.target.value as any)}
                  className="form-select"
                  style={{ padding: '0.32rem 0.36rem', fontSize: '0.7rem' }}
                >
                  <option value="lunch">Lunch (1–5 PM)</option>
                  <option value="dinner">Dinner (8–11 PM)</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.2rem', fontSize: '0.63rem', fontWeight: 600, color: 'hsl(var(--text-muted))', marginBottom: '0.14rem', whiteSpace: 'nowrap' }}>
                  <UsersIcon size={11} /> Guests
                </label>
                <input
                  type="number"
                  min="50"
                  max="1500"
                  step="25"
                  value={guestCount}
                  onChange={(e) => setGuestCount(Number(e.target.value))}
                  className="form-input"
                  style={{ padding: '0.32rem 0.38rem', fontSize: '0.7rem' }}
                />
              </div>
            </div>

            {/* Contact Details (Mandatory) */}
            <div style={{ borderTop: '1px solid hsl(var(--surface-border) / 0.5)', paddingTop: '0.52rem', marginBottom: '0.58rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'nowrap', gap: '0.35rem', marginBottom: '0.42rem' }}>
                <h4 style={{ fontSize: '0.76rem', fontWeight: 800, margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  Customer Contact (72h Hold Voucher)
                </h4>
                <button
                  type="button"
                  className="btn-secondary"
                  style={{ padding: '0.18rem 0.5rem', fontSize: '0.64rem', flexShrink: 0 }}
                  onClick={handleQuickFillDemo}
                >
                  <span>Auto-Fill Sample</span>
                </button>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '0.45rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.64rem', fontWeight: 600, color: 'hsl(var(--text-muted))', marginBottom: '0.14rem' }}>
                    Full Name *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Farhan Siddiqui"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    className="form-input"
                    style={{ padding: '0.36rem 0.55rem', fontSize: '0.75rem' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.64rem', fontWeight: 600, color: 'hsl(var(--text-muted))', marginBottom: '0.14rem' }}>
                    Mobile / WhatsApp *
                  </label>
                  <input
                    type="tel"
                    required
                    placeholder="+92 333 1112233"
                    value={customerPhone}
                    onChange={(e) => setCustomerPhone(e.target.value)}
                    className="form-input"
                    style={{ padding: '0.36rem 0.55rem', fontSize: '0.75rem' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.64rem', fontWeight: 600, color: 'hsl(var(--text-muted))', marginBottom: '0.14rem' }}>
                    Email (PDF Voucher) *
                  </label>
                  <input
                    type="email"
                    required
                    placeholder="farhan@gmail.com"
                    value={customerEmail}
                    onChange={(e) => setCustomerEmail(e.target.value)}
                    className="form-input"
                    style={{ padding: '0.36rem 0.55rem', fontSize: '0.75rem' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.64rem', fontWeight: 600, color: 'hsl(var(--text-muted))', marginBottom: '0.14rem' }}>
                    Visit Notes (Optional)
                  </label>
                  <input
                    type="text"
                    placeholder="Sat 6:30 PM walkthrough"
                    value={specialNotes}
                    onChange={(e) => setSpecialNotes(e.target.value)}
                    className="form-input"
                    style={{ padding: '0.36rem 0.55rem', fontSize: '0.75rem' }}
                  />
                </div>
              </div>
            </div>

            {/* 72-Hour Hold Guarantee Notice */}
            <div
              style={{
                padding: '0.42rem 0.65rem',
                borderRadius: '0.55rem',
                backgroundColor: 'hsl(var(--accent-gold) / 0.1)',
                border: '1px solid hsl(var(--accent-gold) / 0.35)',
                color: 'hsl(var(--text-primary))',
                fontSize: '0.66rem',
                lineHeight: 1.35,
                marginBottom: '0.58rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.4rem'
              }}
            >
              <ShieldCheckIcon size={14} style={{ flexShrink: 0 }} />
              <div>
                <strong>72-Hour Free Hold:</strong> Locks your slot, issues an official <code>MHF-XXXXXX</code> PDF voucher, and alerts the hall manager.
              </div>
            </div>

            {errorMessage && (
              <div
                style={{
                  padding: '0.42rem 0.65rem',
                  borderRadius: '0.45rem',
                  backgroundColor: 'hsl(var(--danger) / 0.15)',
                  border: '1px solid hsl(var(--danger) / 0.4)',
                  color: 'hsl(var(--danger))',
                  fontSize: '0.7rem',
                  marginBottom: '0.55rem'
                }}
              >
                {errorMessage}
              </div>
            )}

            {/* Actions: Dual Voice or Tap Confirmation */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
              <button
                id="confirm-inquiry-btn"
                type="button"
                className="btn-primary"
                style={{
                  width: '100%',
                  height: '2.45rem',
                  fontSize: '0.82rem',
                  backgroundColor: isSuccessPulse ? 'hsl(var(--success))' : undefined
                }}
                disabled={isSubmitting}
                onClick={handleCommit}
              >
                <CheckIcon size={15} />
                <span>{isSubmitting ? 'Issuing Official MHF Voucher...' : 'Confirm & Lock 72-Hour Provisional Hold'}</span>
              </button>
            </div>
          </div>
        </ConicGlowCard>
      </div>
    </div>
  );
};
